import { supabase } from "../lib/supabaseClient";

function throwIfError(error) {
  if (error) {
    throw error;
  }
}

function normalize(value = "") {
  return String(value)
    .trim()
    .toLocaleLowerCase("sv-SE");
}

function isSameClub(candidate, form) {
  const sameName =
    normalize(candidate.name) === normalize(form.clubName);

  if (!sameName) {
    return false;
  }

  const wantedCity = normalize(form.city);
  const candidateCity = normalize(candidate.city);

  // Om användaren inte har angett ort accepterar vi en exakt namnträff.
  if (!wantedCity) {
    return true;
  }

  return wantedCity === candidateCity;
}

/**
 * Söker bland golfklubbar som användaren får läsa enligt RLS.
 * Används av autocomplete i AddCourse.jsx.
 */
export async function searchGolfClubs(searchText) {
  const query = String(searchText ?? "").trim();

  if (query.length < 2) {
    return [];
  }

  const { data, error } = await supabase
    .from("golf_clubs")
    .select(`
      id,
      name,
      city,
      country_code,
      website,
      approval_status,
      created_by
    `)
    .ilike("name", `%${query}%`)
    .order("name", { ascending: true })
    .limit(8);

  throwIfError(error);

  return data ?? [];
}

/**
 * Hämtar golfbanor som användaren får läsa enligt RLS.
 */
export async function fetchGolfCourses() {
  const { data, error } = await supabase
    .from("golf_courses")
    .select(`
      id,
      club_id,
      name,
      hole_count,
      approval_status,
      created_by,
      created_at,
      submitted_at,
      reviewed_at,
      review_note,
      golf_clubs (
        id,
        name,
        city,
        country_code
      ),
      golf_course_tees (
        id,
        name,
        sort_order
      )
    `)
    .order("name", { ascending: true });

  throwIfError(error);

  return data ?? [];
}


/**
 * Hämtar all bandata först när ett kort expanderas eller en egen bana
 * öppnas för redigering.
 *
 * RLS avgör vilka banor användaren får läsa:
 * - approved
 * - egna private/pending/rejected
 */
export async function fetchGolfCourseDetails(courseId) {
  if (!courseId) {
    throw new Error("Ban-ID saknas.");
  }

  const { data: course, error: courseError } = await supabase
    .from("golf_courses")
    .select(`
      id,
      club_id,
      name,
      hole_count,
      approval_status,
      created_by,
      created_at,
      submitted_at,
      reviewed_at,
      review_note,
      source_url,
      source_note,
      golf_clubs (
        id,
        name,
        city,
        country_code,
        website,
        approval_status,
        created_by
      )
    `)
    .eq("id", courseId)
    .single();

  throwIfError(courseError);

  const [
    holesResult,
    teesResult,
    distancesResult,
    ratingsResult,
    handicapsResult,
  ] = await Promise.all([
    supabase
      .from("golf_course_holes")
      .select(`
        id,
        course_id,
        hole_number
      `)
      .eq("course_id", courseId)
      .order("hole_number", { ascending: true }),

    supabase
      .from("golf_course_tees")
      .select(`
        id,
        course_id,
        name,
        sort_order,
        is_active
      `)
      .eq("course_id", courseId)
      .order("sort_order", { ascending: true }),

    supabase
      .from("golf_course_tee_holes")
      .select(`
        course_id,
        tee_id,
        hole_id,
        distance_meters
      `)
      .eq("course_id", courseId),

    supabase
      .from("golf_course_ratings")
      .select(`
        course_id,
        tee_id,
        rating_category,
        start_hole,
        hole_count,
        course_rating,
        slope_rating,
        rating_date
      `)
      .eq("course_id", courseId),

    supabase
      .from("golf_course_hole_handicaps")
      .select(`
        course_id,
        tee_id,
        hole_id,
        rating_category,
        par,
        stroke_index
      `)
      .eq("course_id", courseId),
  ]);

  throwIfError(holesResult.error);
  throwIfError(teesResult.error);
  throwIfError(distancesResult.error);
  throwIfError(ratingsResult.error);
  throwIfError(handicapsResult.error);

  const distances = distancesResult.data ?? [];
  const ratings = ratingsResult.data ?? [];
  const handicaps = handicapsResult.data ?? [];

  const tees = (teesResult.data ?? []).map((tee) => ({
    ...tee,
    distances: distances.filter(
      (row) => row.tee_id === tee.id
    ),
    ratings: ratings.filter(
      (row) => row.tee_id === tee.id
    ),
    handicaps: handicaps.filter(
      (row) => row.tee_id === tee.id
    ),
  }));

  return {
    ...course,
    holes: holesResult.data ?? [],
    tees,
  };
}

function createOwnerEditPayload(form) {
  const tees = form.tees.map((tee, index) => ({
    client_key: String(tee.key),
    name: tee.name.trim(),
    sort_order: index,
  }));

  const holes = form.holes.map((hole) => ({
    hole_number: Number(hole.holeNumber),
    par: Number(hole.par),
    stroke_index: Number(hole.strokeIndex),
  }));

  const distances = [];

  for (const hole of form.holes) {
    for (const tee of form.tees) {
      distances.push({
        tee_key: String(tee.key),
        hole_number: Number(hole.holeNumber),
        distance_meters: Number(
          hole.distances[tee.key]
        ),
      });
    }
  }

  const ratings = [];

  for (const tee of form.tees) {
    const rating = form.ratings[tee.key];

    if (rating?.menEnabled) {
      ratings.push({
        tee_key: String(tee.key),
        rating_category: "men",
        course_rating: Number(rating.menCourseRating),
        slope_rating: Number(rating.menSlopeRating),
        rating_date: rating.menRatingDate || null,
      });
    }

    if (rating?.womenEnabled) {
      ratings.push({
        tee_key: String(tee.key),
        rating_category: "women",
        course_rating: Number(rating.womenCourseRating),
        slope_rating: Number(rating.womenSlopeRating),
        rating_date: rating.womenRatingDate || null,
      });
    }
  }

  return {
    tees,
    holes,
    distances,
    ratings,
  };
}

/**
 * Redigerar en befintlig egen bana.
 *
 * PostgreSQL-funktionen kontrollerar själv att:
 * - auth.uid() äger banan
 * - status är private eller rejected
 *
 * pending och approved kan aldrig redigeras via den här funktionen.
 */
export async function updateOwnGolfCourse({
  courseId,
  form,
  submitForReview = false,
}) {
  if (!courseId) {
    throw new Error("Ban-ID saknas.");
  }

  const payload = createOwnerEditPayload(form);

  const { error } = await supabase.rpc(
    "update_own_editable_golf_course",
    {
      p_course_id: courseId,
      p_name: form.courseName.trim(),
      p_hole_count: Number(form.holeCount),
      p_source_url: form.sourceUrl.trim() || null,
      p_source_note: form.sourceNote.trim() || null,
      p_submit_for_review: Boolean(submitForReview),
      p_tees: payload.tees,
      p_holes: payload.holes,
      p_distances: payload.distances,
      p_ratings: payload.ratings,
    }
  );

  throwIfError(error);

  return {
    courseId,
    submitted: Boolean(submitForReview),
  };
}

async function resolveClub({ form, userId }) {
  // Om användaren valt en befintlig klubb från autocomplete.
  if (form.selectedClubId) {
    const { data, error } = await supabase
      .from("golf_clubs")
      .select(`
        id,
        name,
        city,
        country_code,
        website,
        approval_status,
        created_by
      `)
      .eq("id", form.selectedClubId)
      .single();

    throwIfError(error);

    return {
      club: data,
      createdClubId: null,
    };
  }

  const countryCode = (form.countryCode || "SE")
    .trim()
    .toUpperCase();

  // Skydd mot dubletter om användaren skrivit namnet
  // men inte klickat på autocomplete-förslaget.
  const { data: candidates, error: candidatesError } =
    await supabase
      .from("golf_clubs")
      .select(`
        id,
        name,
        city,
        country_code,
        website,
        approval_status,
        created_by
      `)
      .ilike("name", form.clubName.trim())
      .eq("country_code", countryCode);

  throwIfError(candidatesError);

  const exactMatches = (candidates ?? []).filter((candidate) =>
    isSameClub(candidate, form)
  );

  if (exactMatches.length === 1) {
    return {
      club: exactMatches[0],
      createdClubId: null,
    };
  }

  if (exactMatches.length > 1) {
    throw new Error(
      "Det finns flera golfklubbar med det namnet. Välj rätt klubb i förslagslistan."
    );
  }

  // Klubben finns inte -> skapa privat klubb.
  const { data: insertedClub, error: clubInsertError } =
    await supabase
      .from("golf_clubs")
      .insert({
        name: form.clubName.trim(),
        city: form.city.trim() || null,
        country_code: countryCode,
        website: form.website.trim() || null,
        created_by: userId,
        approval_status: "private",
      })
      .select(`
        id,
        name,
        city,
        country_code,
        website,
        approval_status,
        created_by
      `)
      .single();

  throwIfError(clubInsertError);

  return {
    club: insertedClub,
    createdClubId: insertedClub.id,
  };
}

/**
 * Skapar hela banstrukturen.
 *
 * All data skapas först som private.
 * Om submitForReview=true ändras banan till pending först efter
 * att all underliggande data har sparats.
 */
export async function createGolfCourse({
  form,
  userId,
  submitForReview = false,
}) {
  if (!userId) {
    throw new Error("Ingen inloggad användare hittades.");
  }

  let createdClubId = null;
  let createdCourseId = null;

  try {
    // -------------------------------------------------------
    // 1. Klubb
    // -------------------------------------------------------
    const clubResult = await resolveClub({
      form,
      userId,
    });

    const club = clubResult.club;
    createdClubId = clubResult.createdClubId;

    // -------------------------------------------------------
    // 2. Bana
    // -------------------------------------------------------
    const { data: course, error: courseError } = await supabase
      .from("golf_courses")
      .insert({
        club_id: club.id,
        name: form.courseName.trim(),
        hole_count: Number(form.holeCount),
        created_by: userId,
        approval_status: "private",
        source_url: form.sourceUrl.trim() || null,
        source_note: form.sourceNote.trim() || null,
      })
      .select("id")
      .single();

    throwIfError(courseError);

    createdCourseId = course.id;

    // -------------------------------------------------------
    // 3. Hål
    // -------------------------------------------------------
    const { data: insertedHoles, error: holesError } =
      await supabase
        .from("golf_course_holes")
        .insert(
          form.holes.map((hole) => ({
            course_id: course.id,
            hole_number: Number(hole.holeNumber),
          }))
        )
        .select("id, hole_number");

    throwIfError(holesError);

    const holeByNumber = new Map(
      (insertedHoles ?? []).map((hole) => [
        Number(hole.hole_number),
        hole,
      ])
    );

    // -------------------------------------------------------
    // 4. Tees
    // -------------------------------------------------------
    const { data: insertedTees, error: teesError } =
      await supabase
        .from("golf_course_tees")
        .insert(
          form.tees.map((tee, index) => ({
            course_id: course.id,
            name: tee.name.trim(),
            color: null,
            sort_order: index,
            is_active: true,
          }))
        )
        .select("id, name, sort_order");

    throwIfError(teesError);

    const teeByClientKey = new Map();

    form.tees.forEach((tee, index) => {
      const dbTee = (insertedTees ?? []).find(
        (candidate) => Number(candidate.sort_order) === index
      );

      if (!dbTee) {
        throw new Error(
          `HoleInOne kunde inte koppla tee "${tee.name}" efter att den skapades.`
        );
      }

      teeByClientKey.set(tee.key, dbTee);
    });

    // -------------------------------------------------------
    // 5. Längd per tee och hål
    // -------------------------------------------------------
    const teeHoleRows = [];

    for (const hole of form.holes) {
      const dbHole = holeByNumber.get(Number(hole.holeNumber));

      if (!dbHole) {
        throw new Error(
          `HoleInOne kunde inte koppla hål ${hole.holeNumber}.`
        );
      }

      for (const tee of form.tees) {
        const dbTee = teeByClientKey.get(tee.key);

        teeHoleRows.push({
          course_id: course.id,
          tee_id: dbTee.id,
          hole_id: dbHole.id,
          distance_meters: Number(hole.distances[tee.key]),
        });
      }
    }

    const { error: teeHolesError } = await supabase
      .from("golf_course_tee_holes")
      .insert(teeHoleRows);

    throwIfError(teeHolesError);

    // -------------------------------------------------------
    // 6. Course Rating + Slope
    // -------------------------------------------------------
    const ratingRows = [];
    const activeCategoriesByTee = new Map();

    for (const tee of form.tees) {
      const dbTee = teeByClientKey.get(tee.key);
      const rating = form.ratings[tee.key];
      const categories = [];

      if (rating.menEnabled) {
        categories.push("men");

        ratingRows.push({
          course_id: course.id,
          tee_id: dbTee.id,
          rating_category: "men",
          start_hole: 1,
          hole_count: Number(form.holeCount),
          course_rating: Number(rating.menCourseRating),
          slope_rating: Number(rating.menSlopeRating),
        });
      }

      if (rating.womenEnabled) {
        categories.push("women");

        ratingRows.push({
          course_id: course.id,
          tee_id: dbTee.id,
          rating_category: "women",
          start_hole: 1,
          hole_count: Number(form.holeCount),
          course_rating: Number(rating.womenCourseRating),
          slope_rating: Number(rating.womenSlopeRating),
        });
      }

      activeCategoriesByTee.set(tee.key, categories);
    }

    if (ratingRows.length === 0) {
      throw new Error(
        "Minst en tee måste ha en giltig Course Rating och Slope Rating."
      );
    }

    const { error: ratingsError } = await supabase
      .from("golf_course_ratings")
      .insert(ratingRows);

    throwIfError(ratingsError);

    // -------------------------------------------------------
    // 7. Par + stroke index
    // -------------------------------------------------------
    const handicapRows = [];

    for (const hole of form.holes) {
      const dbHole = holeByNumber.get(Number(hole.holeNumber));

      for (const tee of form.tees) {
        const dbTee = teeByClientKey.get(tee.key);
        const categories =
          activeCategoriesByTee.get(tee.key) ?? [];

        for (const ratingCategory of categories) {
          handicapRows.push({
            course_id: course.id,
            tee_id: dbTee.id,
            hole_id: dbHole.id,
            rating_category: ratingCategory,
            par: Number(hole.par),
            stroke_index: Number(hole.strokeIndex),
          });
        }
      }
    }

    const { error: handicapsError } = await supabase
      .from("golf_course_hole_handicaps")
      .insert(handicapRows);

    throwIfError(handicapsError);

    // -------------------------------------------------------
    // 8. Skicka till granskning
    // -------------------------------------------------------
    if (submitForReview) {
      const submittedAt = new Date().toISOString();

      const { error: courseSubmitError } = await supabase
        .from("golf_courses")
        .update({
          approval_status: "pending",
          submitted_at: submittedAt,
        })
        .eq("id", course.id);

      throwIfError(courseSubmitError);

      if (
        createdClubId &&
        club.created_by === userId &&
        club.approval_status !== "approved"
      ) {
        const { error: clubSubmitError } = await supabase
          .from("golf_clubs")
          .update({
            approval_status: "pending",
            submitted_at: submittedAt,
          })
          .eq("id", club.id);

        throwIfError(clubSubmitError);
      }
    }

    return {
      courseId: course.id,
      clubId: club.id,
      submitted: submitForReview,
    };
  } catch (error) {
    // Cleanup om något går fel medan posterna fortfarande är private.
    if (createdCourseId) {
      try {
        await supabase
          .from("golf_courses")
          .delete()
          .eq("id", createdCourseId);
      } catch {
        // Behåll originalfelet.
      }
    }

    if (createdClubId) {
      try {
        await supabase
          .from("golf_clubs")
          .delete()
          .eq("id", createdClubId);
      } catch {
        // Behåll originalfelet.
      }
    }

    throw error;
  }
}